import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { varietiesApi, storeApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Store, Plus, Pencil, Trash2, ImagePlus, X, ExternalLink, Leaf, Loader2, Truck, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { VarietyFormDialog } from './varieties/VarietyFormDialog';
import { DeliveryZonesPanel } from './varieties/DeliveryZonesPanel';
import { InvoiceSettingsPanel } from './varieties/InvoiceSettingsPanel';

function PhotoStrip({ variety, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await varietiesApi.uploadPhoto(variety.id, fd);
      }
      toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} uploaded`);
      onChange();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const removePhoto = async (path) => {
    try { await varietiesApi.deletePhoto(variety.id, path); onChange(); }
    catch { toast.error('Could not remove photo'); }
  };

  return (
    <div className="flex gap-2 flex-wrap items-center" data-testid={`photos-${variety.slug}`}>
      {variety.photos.map((p) => (
        <div key={p} className="relative w-16 h-16 rounded-md overflow-hidden group border">
          <img src={storeApi.photoUrl(p)} alt="" className="w-full h-full object-cover" />
          <button onClick={() => removePhoto(p)} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-photo-btn"><X className="h-3 w-3" /></button>
        </div>
      ))}
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid={`photo-input-${variety.slug}`} />
      <button onClick={() => inputRef.current?.click()} disabled={uploading} className="w-16 h-16 rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-[10px] gap-1" data-testid={`upload-photo-btn-${variety.slug}`}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {uploading ? '' : 'Add'}
      </button>
    </div>
  );
}

export function VarietiesPage() {
  const [params] = useSearchParams();
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await varietiesApi.getAll(); setVarieties(data); }
    catch { toast.error('Failed to load varieties'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (payload) => {
    try {
      if (editing) await varietiesApi.update(editing.id, payload); else await varietiesApi.create(payload);
      toast.success(editing ? 'Variety updated' : 'Variety added');
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete ${v.name}? This removes it from the website.`)) return;
    try { await varietiesApi.delete(v.id); toast.success('Variety deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggle = async (v, key) => {
    try { await varietiesApi.update(v.id, { ...v, [key]: !v[key] }); load(); }
    catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="varieties-loading" />;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="varieties-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3"><Store className="h-8 w-8 text-primary" /> Turf Shop</h1>
          <p className="text-muted-foreground">Manage the varieties, prices, photos and delivery zones customers see on your website</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild><a href="/" target="_blank" rel="noreferrer" data-testid="open-shop-btn"><ExternalLink className="h-4 w-4 mr-2" /> View website</a></Button>
          <Button className="btn-primary" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-variety-btn"><Plus className="h-4 w-4 mr-2" /> Add variety</Button>
        </div>
      </div>

      <Tabs defaultValue={params.get('tab') || 'varieties'} className="space-y-6">
        <TabsList data-testid="shop-tabs">
          <TabsTrigger value="varieties" data-testid="tab-varieties"><Leaf className="h-4 w-4 mr-2" /> Varieties ({varieties.length})</TabsTrigger>
          <TabsTrigger value="zones" data-testid="tab-delivery-zones"><Truck className="h-4 w-4 mr-2" /> Delivery Zones</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoice-settings"><FileText className="h-4 w-4 mr-2" /> Invoice Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="varieties">
      {varieties.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground"><Leaf className="h-10 w-10 mx-auto mb-3 text-primary" />No varieties yet. Add your first turf variety.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {varieties.map((v) => (
            <Card key={v.id} className={!v.active ? 'opacity-60' : ''} data-testid={`variety-row-${v.slug}`}>
              <CardContent className="p-5 grid lg:grid-cols-[1fr_auto] gap-5">
                <div className="space-y-3 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading font-bold text-lg">{v.name}</h3>
                    <Badge variant="secondary">${v.price_per_m2.toFixed(2)}/m²</Badge>
                    <Badge variant="outline">min {v.min_order_m2} m²</Badge>
                    <button onClick={() => toggle(v, 'active')} className={`text-xs px-2 py-0.5 rounded-full font-medium ${v.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`} data-testid={`toggle-active-${v.slug}`}>{v.active ? 'Live on website' : 'Hidden'}</button>
                    <button onClick={() => toggle(v, 'in_stock')} className={`text-xs px-2 py-0.5 rounded-full font-medium ${v.in_stock ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-testid={`toggle-stock-${v.slug}`}>{v.in_stock ? 'In stock' : 'Out of stock'}</button>
                  </div>
                  {v.description && <p className="text-sm text-muted-foreground line-clamp-2">{v.description}</p>}
                  <PhotoStrip variety={v} onChange={load} />
                </div>
                <div className="flex lg:flex-col gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => { setEditing(v); setDialogOpen(true); }} data-testid={`edit-variety-${v.slug}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(v)} data-testid={`delete-variety-${v.slug}`}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </TabsContent>

        <TabsContent value="zones">
          <DeliveryZonesPanel />
        </TabsContent>

        <TabsContent value="invoices">
          <InvoiceSettingsPanel />
        </TabsContent>
      </Tabs>

      <VarietyFormDialog open={dialogOpen} onOpenChange={setDialogOpen} variety={editing} onSave={handleSave} />
    </div>
  );
}
